import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { BookingModal } from '../components/shared/BookingModal';
import { LeadForm } from '../components/shared/LeadForm';
import { ArrowRight, CheckCircle, HeartPulse, Building2, Plane, FileCheck, Store, Star, TrendingUp } from 'lucide-react';

const industriesData = {
    'healthcare-seo': {
        icon: HeartPulse,
        name: 'Healthcare SEO',
        headline: 'Help Patients Find Your Hospital or Clinic',
        subheadline: 'Dominate healthcare searches and build trust with patients looking for medical services.',
        description: 'Healthcare is one of the most competitive and regulated industries for SEO. Patients are actively searching for doctors, hospitals, and medical services. We help healthcare providers rank for these searches while maintaining YMYL (Your Money Your Life) compliance.',
        painPoints: [
            'Struggling to rank against larger hospital chains',
            'Low visibility in Google Maps for local patients',
            'Difficulty building trust and credibility online',
            'Managing online reputation and reviews',
            'Competing for specialist and treatment keywords',
        ],
        solutions: [
            'Doctor and specialist profile optimization',
            'Multi-location hospital SEO',
            'Patient review generation systems',
            'Medical content that builds authority',
            'Local SEO for catchment areas',
            'HIPAA-compliant digital strategies',
        ],
        results: {
            patient_inquiries: '+180%',
            appointment_bookings: '+150%',
            map_visibility: '#1 in 8/10 specialties',
        },
        useCases: [
            'A multi-specialty hospital increased patient inquiries by 200% in 6 months',
            'A dental clinic chain ranked #1 in maps for 12 locations',
            'A fertility center became the top organic result for IVF-related searches',
        ],
        services: ['GMB Optimization', 'Local SEO', 'Content Marketing', 'Reputation Management'],
        color: 'from-red-500 to-rose-500',
    },
    'clinic-seo': {
        icon: Building2,
        name: 'Clinic SEO',
        headline: 'Fill Your Appointment Book with Qualified Patients',
        subheadline: 'Rank in local searches and Google Maps to attract patients in your area.',
        description: 'For clinics, local visibility is everything. Whether you\'re a dental clinic, physiotherapy center, or aesthetic clinic, patients are searching for services "near me". We optimize your online presence to capture these high-intent local searches.',
        painPoints: [
            'Lost to competitors in Google Maps',
            'Inconsistent online information (NAP)',
            'Few or negative online reviews',
            'Website not attracting patients',
            'Limited visibility for specialty services',
        ],
        solutions: [
            'Complete GMB profile optimization',
            'Review generation and management',
            'Local citation building',
            'Service-specific landing pages',
            'Before/after gallery optimization',
            'Appointment booking integration',
        ],
        results: {
            map_rankings: '#1 for primary services',
            new_patients: '+85% monthly',
            reviews: '500+ 5-star reviews generated',
        },
        useCases: [
            'A dental clinic went from page 3 to #1 in Google Maps within 3 months',
            'An aesthetic clinic increased consultation bookings by 120%',
            'A physiotherapy center dominates "near me" searches in their city',
        ],
        services: ['GMB Optimization', 'Local SEO', 'Review Management'],
        color: 'from-blue-500 to-indigo-500',
    },
    'travel-seo': {
        icon: Plane,
        name: 'Travel SEO',
        headline: 'Rank for Destination and Package Keywords',
        subheadline: 'Compete with OTAs and capture travelers searching for their next adventure.',
        description: 'Travel SEO is about capturing wanderlust at the right moment. From destination guides to package comparisons, we help travel businesses rank for the searches that lead to bookings, not just browsing.',
        painPoints: [
            'Competing against giant OTAs (MakeMyTrip, Booking.com)',
            'Seasonal traffic fluctuations',
            'Low conversion from organic traffic',
            'Difficulty ranking for destination keywords',
            'Visual content not showing in search',
        ],
        solutions: [
            'Destination-specific landing pages',
            'Package comparison content',
            'Travel guide creation',
            'Image and video SEO',
            'Seasonal content planning',
            'Review and testimonial optimization',
        ],
        results: {
            organic_traffic: '+520%',
            bookings: '+290% from SEO',
            keywords: '500+ page 1 rankings',
        },
        useCases: [
            'A tour operator ranks #1 for "best Ladakh packages" beating OTAs',
            'A travel agency increased bookings 3x through organic traffic',
            'A honeymoon specialist dominates destination keywords',
        ],
        services: ['High-Intent SEO', 'Content Marketing', 'Programmatic SEO'],
        color: 'from-sky-500 to-cyan-500',
    },
    'visa-agency-seo': {
        icon: FileCheck,
        name: 'Visa Agency SEO',
        headline: 'Capture Visa Seekers Searching Online',
        subheadline: 'Build trust and rank for visa-related searches across all countries.',
        description: 'Visa applicants are highly motivated searchers. They need answers and they need help. We position your visa agency as the trusted authority for visa services, capturing leads at every stage of the application journey.',
        painPoints: [
            'Trust issues in the visa industry',
            'Competing for country-specific keywords',
            'Complex documentation queries',
            'Seasonal visa demand patterns',
            'Multi-language audience targeting',
        ],
        solutions: [
            'Country-specific visa landing pages',
            'Documentation guide content',
            'FAQ schema implementation',
            'Trust signal optimization',
            'Multi-city GEO SEO',
            'Review and success story showcasing',
        ],
        results: {
            leads: '+150% monthly',
            trust_signals: '200+ testimonials',
            countries: '50+ visa pages ranked',
        },
        useCases: [
            'A visa consultancy ranks #1 for "Canada visa consultants" in 5 cities',
            'A student visa agency increased leads 2x with content marketing',
            'An immigration firm dominates niche visa categories',
        ],
        services: ['GEO SEO', 'Content Marketing', 'GMB Optimization'],
        color: 'from-emerald-500 to-green-500',
    },
    'local-business-seo': {
        icon: Store,
        name: 'Local Business SEO',
        headline: 'Be the #1 Choice in Your Neighborhood',
        subheadline: 'Dominate local search and Google Maps to capture customers in your area.',
        description: 'For local businesses, being found online often means the difference between thriving and closing. We help restaurants, salons, gyms, and service businesses become the obvious choice when locals search.',
        painPoints: [
            'Invisible on Google Maps',
            'Losing customers to better-ranked competitors',
            'Few online reviews',
            'Website not generating leads',
            'Inconsistent online presence',
        ],
        solutions: [
            'Google Business Profile domination',
            'Local citation building',
            'Review generation systems',
            'Local content marketing',
            'Neighborhood targeting',
            'Mobile optimization',
        ],
        results: {
            map_rankings: '#1 in local pack',
            calls: '+200% from GMB',
            foot_traffic: '+150% increase',
        },
        useCases: [
            'A restaurant chain became #1 on Maps for all 8 locations',
            'A gym increased memberships 85% through local SEO',
            'A salon dominates "near me" searches in their city',
        ],
        services: ['GMB Optimization', 'Local SEO', 'Review Management'],
        color: 'from-orange-500 to-amber-500',
    },
};

export default function IndustryPage() {
    const location = useLocation();
    const industrySlug = location.pathname.slice(1); // Remove leading slash
    const [bookingOpen, setBookingOpen] = useState(false);
    
    const industry = industriesData[industrySlug];
    
    if (!industry) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-4xl font-bold mb-4">Industry Not Found</h1>
                    <Button asChild className="rounded-full">
                        <Link to="/services">View All Services</Link>
                    </Button>
                </div>
            </div>
        );
    }

    const IndustryIcon = industry.icon;

    return (
        <div className="relative" data-testid={`industry-page-${industrySlug}`}>
            {/* Hero */}
            <section className="section-padding relative overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${industry.color} opacity-5`} />
                <div className="container-custom relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-6">
                                <IndustryIcon className="w-4 h-4" />
                                <span>{industry.name}</span>
                            </div>
                            
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                                {industry.headline}
                            </h1>
                            
                            <p className="text-lg md:text-xl text-muted-foreground mb-8">
                                {industry.subheadline}
                            </p>

                            <Button 
                                size="lg"
                                className="btn-glow rounded-full px-8"
                                onClick={() => setBookingOpen(true)}
                                data-testid="industry-hero-cta"
                            >
                                Get Free Industry Audit
                                <ArrowRight className="w-5 h-5 ml-2" />
                            </Button>
                        </div>

                        {/* Results Card */}
                        <div className="lg:pl-8">
                            <div className={`rounded-3xl bg-gradient-to-br ${industry.color} p-px`}>
                                <div className="rounded-3xl bg-background p-8">
                                    <h3 className="text-xl font-semibold mb-6">Typical Results</h3>
                                    <div className="space-y-4">
                                        {Object.entries(industry.results).map(([key, value]) => (
                                            <div key={key} className="flex justify-between items-center">
                                                <span className="text-muted-foreground capitalize">
                                                    {key.replace(/_/g, ' ')}
                                                </span>
                                                <span className="text-xl font-bold text-primary">{value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Description */}
            <section className="section-padding bg-muted/30">
                <div className="container-custom">
                    <div className="max-w-3xl">
                        <h2 className="text-3xl font-semibold tracking-tight mb-6">
                            Understanding {industry.name}
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed">
                            {industry.description}
                        </p>
                    </div>
                </div>
            </section>

            {/* Pain Points & Solutions */}
            <section className="section-padding">
                <div className="container-custom">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                        {/* Pain Points */}
                        <div>
                            <h2 className="text-2xl font-semibold tracking-tight mb-8">
                                Common Challenges
                            </h2>
                            <div className="space-y-4">
                                {industry.painPoints.map((point, index) => (
                                    <div 
                                        key={index}
                                        className="flex items-start gap-3 p-4 rounded-xl bg-destructive/5 border border-destructive/10"
                                    >
                                        <span className="w-6 h-6 rounded-full bg-destructive/10 text-destructive flex items-center justify-center text-sm flex-shrink-0">
                                            {index + 1}
                                        </span>
                                        <span>{point}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Solutions */}
                        <div>
                            <h2 className="text-2xl font-semibold tracking-tight mb-8">
                                Our Solutions
                            </h2>
                            <div className="space-y-4">
                                {industry.solutions.map((solution, index) => (
                                    <div 
                                        key={index}
                                        className="flex items-center gap-3 p-4 rounded-xl bg-primary/5 border border-primary/10"
                                    >
                                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                                        <span>{solution}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Use Cases */}
            <section className="section-padding bg-muted/30">
                <div className="container-custom">
                    <h2 className="text-3xl font-semibold tracking-tight mb-12 text-center">
                        Success Stories
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {industry.useCases.map((useCase, index) => (
                            <div 
                                key={index}
                                className="p-6 rounded-2xl border border-border bg-background"
                            >
                                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                                    <TrendingUp className="w-5 h-5 text-primary" />
                                </div>
                                <p className="text-muted-foreground">{useCase}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Used */}
            <section className="section-padding">
                <div className="container-custom">
                    <h2 className="text-3xl font-semibold tracking-tight mb-8 text-center">
                        Recommended Services
                    </h2>
                    <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
                        Based on our experience with {industry.name.toLowerCase()}, we recommend these services.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        {industry.services.map((service, index) => (
                            <Link
                                key={index}
                                to="/services"
                                className="px-6 py-3 rounded-full border border-border bg-background hover:border-primary/30 hover:bg-primary/5 transition-colors font-medium"
                            >
                                {service}
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Lead Form Section */}
            <section className="section-padding bg-foreground text-background">
                <div className="container-custom">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                                Get Your Free {industry.name} Audit
                            </h2>
                            <p className="text-background/70 mb-8">
                                We'll analyze your current search presence and show you exactly 
                                how to outrank your competitors.
                            </p>
                            
                            <div className="space-y-4">
                                {[
                                    'Complete visibility analysis',
                                    'Competitor comparison',
                                    'Quick win opportunities',
                                    'Custom strategy roadmap',
                                ].map((item, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <CheckCircle className="w-5 h-5 text-primary" />
                                        <span>{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="lg:pl-8">
                            <div className="p-8 rounded-3xl bg-background text-foreground">
                                <LeadForm sourcePage={industrySlug} compact />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="section-padding">
                <div className="container-custom">
                    <h2 className="text-3xl font-semibold tracking-tight mb-12 text-center">
                        {industry.name} FAQs
                    </h2>
                    <div className="max-w-3xl mx-auto space-y-4">
                        {[
                            { q: `How long does it take to see results for ${industry.name.toLowerCase()}?`, a: 'Most clients see initial improvements within 2-3 months, with significant results in 4-6 months.' },
                            { q: 'Do you have experience with my specific type of business?', a: `Yes, we've worked with numerous ${industry.name.toLowerCase()} businesses and understand the unique challenges.` },
                            { q: 'What makes your approach different?', a: 'We combine traditional SEO with AI search optimization and local dominance strategies tailored for your industry.' },
                        ].map((faq, index) => (
                            <div 
                                key={index}
                                className="p-6 rounded-2xl border border-border bg-background"
                            >
                                <h3 className="font-semibold mb-2">{faq.q}</h3>
                                <p className="text-muted-foreground">{faq.a}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="section-padding bg-muted/30">
                <div className="container-custom text-center">
                    <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
                        Ready to Dominate {industry.name}?
                    </h2>
                    <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
                        Let's discuss your goals and create a custom strategy that delivers results.
                    </p>
                    <Button 
                        size="lg"
                        className="btn-glow rounded-full px-8"
                        onClick={() => setBookingOpen(true)}
                        data-testid="industry-bottom-cta"
                    >
                        Book Free Strategy Call
                        <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                </div>
            </section>

            <BookingModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
        </div>
    );
}
